import { ReactNode, useEffect, useRef, useState } from "react";
import { Button, Label, Select, TextInput } from "flowbite-react";
import {
  HiAdjustments,
  HiChevronDown,
  HiDocumentDownload,
  HiSearch,
  HiX,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { VectorizeStatusType } from "@/sharedTypes";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useGetSubcategoriesListQuery } from "@/api/ticketApi/subcategory";

type SelectOption = { label: string; value: string };

const VECTORIZE_STATUSES: VectorizeStatusType[] = [
  "not_vectorized",
  "pending",
  "processing",
  "vectorized",
  "failed",
];

const SORT_OPTIONS = [
  { value: "createdAt:DESC", key: "newestCreated" },
  { value: "createdAt:ASC", key: "oldestCreated" },
  { value: "updatedAt:DESC", key: "newestUpdated" },
  { value: "updatedAt:ASC", key: "oldestUpdated" },
];

interface KbFilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;

  vectorizeStatus: string;
  aiApproved: string;
  onVectorizeStatusChange: (v: string) => void;
  onAiApprovedChange: (v: string) => void;
  showVectorize?: boolean;

  /** Enables the category / subcategory filter (only where the API supports it) */
  showCategory?: boolean;
  categoryId?: string;
  subcategoryId?: string;
  onCategoryChange?: (v: string) => void;
  onSubcategoryChange?: (v: string) => void;

  /** Sort as "field:ORDER"; omit handler to hide sort */
  sort?: string;
  onSortChange?: (v: string) => void;

  /** Enables the PDF report date-range section */
  enableReport?: boolean;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (v: string) => void;
  onEndDateChange?: (v: string) => void;
  onDownload?: () => void;
  isDownloading?: boolean;

  /** Right-aligned actions (e.g. create button) */
  actions?: ReactNode;
}

export default function KbFilterBar({
  search,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  vectorizeStatus,
  aiApproved,
  onVectorizeStatusChange,
  onAiApprovedChange,
  showVectorize = true,
  showCategory = false,
  categoryId = "",
  subcategoryId = "",
  onCategoryChange,
  onSubcategoryChange,
  sort,
  onSortChange,
  enableReport = false,
  startDate = "",
  endDate = "",
  onStartDateChange,
  onEndDateChange,
  onDownload,
  isDownloading,
  actions,
}: KbFilterBarProps) {
  const { t } = useTranslation("knowledgeBase");
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Category / subcategory pickers. The selected options are kept here (not in
  // the parent) so the chips can show names instead of raw ids; the parent only
  // ever receives the id.
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SelectOption | null>(null);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [subcategorySearch, setSubcategorySearch] = useState("");

  const { data: categoriesData, isFetching: isCategoriesFetching } =
    useGetPaginatedCategoriesQuery(
      {
        pagination: { page: categoryPage, pageSize: 10 },
        filters: { "category_name[like]": categorySearch },
      },
      { skip: !showCategory }
    );

  // Subcategories are only meaningful once a category is picked.
  const { data: subcategoriesData, isFetching: isSubcategoriesFetching } =
    useGetSubcategoriesListQuery(
      {
        pagination: { page: subcategoryPage, pageSize: 10 },
        filters: {
          "subcategory_name[like]": subcategorySearch,
          category_id: categoryId || undefined,
        },
      },
      { skip: !showCategory || !categoryId }
    );

  const categoryOptions: SelectOption[] = (categoriesData?.items ?? []).map((c: any) => ({
    label: c.category_name,
    value: String(c.category_id),
  }));
  const subcategoryOptions: SelectOption[] = (subcategoriesData?.items ?? []).map((sc: any) => ({
    label: sc.subcategory_name,
    value: String(sc.subcategory_id),
  }));

  // Keep the local selection in sync when the parent clears the filter itself.
  useEffect(() => {
    if (!categoryId) {
      setSelectedCategory(null);
      setCategorySearch("");
      setCategoryPage(1);
    }
  }, [categoryId]);
  useEffect(() => {
    if (!subcategoryId) {
      setSelectedSubcategory(null);
      setSubcategorySearch("");
      setSubcategoryPage(1);
    }
  }, [subcategoryId]);

  const handleCategoryChange = (option: SelectOption | null) => {
    setSelectedCategory(option);
    onCategoryChange?.(option?.value ?? "");
    // A subcategory from the previous category would filter everything out.
    setSelectedSubcategory(null);
    setSubcategorySearch("");
    setSubcategoryPage(1);
    onSubcategoryChange?.("");
  };

  // Close the popover on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // The select menus render in a body portal, so a click on an option is
      // technically outside the popover — don't treat it as one.
      if (target?.closest?.(".paginated-select__menu")) return;
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Active-filter chips (sort is excluded — it always has a value).
  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (showVectorize && vectorizeStatus) {
    chips.push({
      key: "vectorize",
      label: `${t("vectorized")}: ${t(`vectorizeStatus.${vectorizeStatus}`)}`,
      onRemove: () => onVectorizeStatusChange(""),
    });
  }
  if (aiApproved) {
    chips.push({
      key: "aiApproved",
      label: `${t("aiApproved")}: ${aiApproved === "1" ? t("yes") : t("no")}`,
      onRemove: () => onAiApprovedChange(""),
    });
  }
  if (showCategory && categoryId) {
    chips.push({
      key: "category",
      label: `${t("category")}: ${selectedCategory?.label ?? `#${categoryId}`}`,
      onRemove: () => handleCategoryChange(null),
    });
  }
  if (showCategory && subcategoryId) {
    chips.push({
      key: "subcategory",
      label: `${t("subcategory")}: ${selectedSubcategory?.label ?? `#${subcategoryId}`}`,
      onRemove: () => {
        setSelectedSubcategory(null);
        onSubcategoryChange?.("");
      },
    });
  }
  if (enableReport && (startDate || endDate)) {
    chips.push({
      key: "dateRange",
      label: `${startDate || "…"} – ${endDate || "…"}`,
      onRemove: () => {
        onStartDateChange?.("");
        onEndDateChange?.("");
      },
    });
  }
  const activeCount = chips.length;

  const clearAll = () => chips.forEach((c) => c.onRemove());

  return (
    <div className="mb-4">
      {/* Toolbar row */}
      <div className="flex flex-wrap items-center gap-3">
        {showSearch && (
          <div className="relative w-full max-w-sm flex-1">
            <TextInput
              icon={HiSearch}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder ?? t("search")}
            />
          </div>
        )}

        {/* Filter popover */}
        <div className="relative" ref={popoverRef}>
          <Button color="light" onClick={() => setOpen((o) => !o)}>
            <HiAdjustments className="mr-2 h-5 w-5" />
            {t("filters")}
            {activeCount > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white">
                {activeCount}
              </span>
            )}
            <HiChevronDown className="ml-2 h-4 w-4" />
          </Button>

          {open && (
            <div className="absolute right-0 z-30 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              <div className="space-y-4">
                {showCategory && (
                  <>
                    <div>
                      <Label className="mb-1 block">{t("category")}</Label>
                      <PaginatedSelect
                        id="kb-category-filter"
                        value={selectedCategory}
                        onChange={(v) => handleCategoryChange(v as SelectOption | null)}
                        options={categoryOptions}
                        onInputChange={(v) => {
                          setCategorySearch(v);
                          setCategoryPage(1);
                        }}
                        isFetching={isCategoriesFetching}
                        placeholder={t("all")}
                        page={categoryPage}
                        totalPages={categoriesData?.pagination?.totalPages || 1}
                        onPageChange={setCategoryPage}
                        isClearable
                        menuMaxHeight={220}
                      />
                    </div>

                    <div>
                      <Label className="mb-1 block">{t("subcategory")}</Label>
                      <PaginatedSelect
                        id="kb-subcategory-filter"
                        value={selectedSubcategory}
                        onChange={(v) => {
                          const option = v as SelectOption | null;
                          setSelectedSubcategory(option);
                          onSubcategoryChange?.(option?.value ?? "");
                        }}
                        options={subcategoryOptions}
                        onInputChange={(v) => {
                          setSubcategorySearch(v);
                          setSubcategoryPage(1);
                        }}
                        isFetching={isSubcategoriesFetching}
                        placeholder={categoryId ? t("all") : t("selectCategory")}
                        page={subcategoryPage}
                        totalPages={subcategoriesData?.pagination?.totalPages || 1}
                        onPageChange={setSubcategoryPage}
                        isClearable
                        isDisabled={!categoryId}
                        menuMaxHeight={220}
                      />
                    </div>
                  </>
                )}

                {showVectorize && (
                  <div>
                    <Label className="mb-1 block">{t("vectorized")}</Label>
                    <Select
                      value={vectorizeStatus}
                      onChange={(e) => onVectorizeStatusChange(e.target.value)}
                    >
                      <option value="">{t("all")}</option>
                      {VECTORIZE_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`vectorizeStatus.${s}`)}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="mb-1 block">{t("aiApproved")}</Label>
                  <Select
                    value={aiApproved}
                    onChange={(e) => onAiApprovedChange(e.target.value)}
                  >
                    <option value="">{t("all")}</option>
                    <option value="1">{t("yes")}</option>
                    <option value="0">{t("no")}</option>
                  </Select>
                </div>

                {onSortChange && (
                  <div>
                    <Label className="mb-1 block">{t("sortBy")}</Label>
                    <Select
                      value={sort}
                      onChange={(e) => onSortChange(e.target.value)}
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {t(`sort.${o.key}`)}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {enableReport && (
                  <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1 block">{t("startDate")}</Label>
                        <TextInput
                          type="date"
                          value={startDate}
                          max={endDate || undefined}
                          onChange={(e) => onStartDateChange?.(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="mb-1 block">{t("endDate")}</Label>
                        <TextInput
                          type="date"
                          value={endDate}
                          min={startDate || undefined}
                          onChange={(e) => onEndDateChange?.(e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      color="light"
                      className="w-full"
                      onClick={onDownload}
                      disabled={isDownloading}
                    >
                      <HiDocumentDownload className="mr-2 h-5 w-5" />
                      {isDownloading ? t("downloadingReport") : t("downloadReport")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>

      {/* Active filter chips */}
      {activeCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.onRemove}
                className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800"
                aria-label={`${t("clearFilters")} ${chip.label}`}
              >
                <HiX className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-sm font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
          >
            {t("clearFilters")}
          </button>
        </div>
      )}
    </div>
  );
}
